import { useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { redirectToHostedUi } from '@goodsteward/gs-auth-spa';
import { useAuthStore } from '@/stores/auth';
import { getCognitoConfig } from '@/auth/config';

// Auth gate for every protected route. When there's no in-memory
// token, redirects to the IdP — the local stub at :9000 in dev,
// Cognito Hosted UI in test/prod. The auth store is persisted to
// localStorage, so the redirect only fires on a true cold start
// (first visit, or after explicit logout); reloads with a live
// token continue without round-tripping the IdP.
//
// Permission gates send the user to / on a missed perm — the
// dashboard is always reachable for any signed-in user, so it's a
// safe fallback that doesn't loop through the auth gate.

export function RequireAuth({
    children,
    perm,
}: {
    children: React.ReactNode;
    perm?: string;
}) {
    const token = useAuthStore((s) => s.token);
    const user = useAuthStore((s) => s.user);
    const logout = useAuthStore((s) => s.logout);
    const hasPerm = useAuthStore((s) => s.hasPermission);
    const location = useLocation();
    const navigate = useNavigate();
    const triedRef = useRef(false);

    useEffect(() => {
        // Half-state recovery: token persisted but no profile attached
        // (e.g. the auth callback failed loading /api/me, or
        // localStorage was edited). Render-time we'd let the user in
        // and the shell would fire 403s with no nav out — clear the
        // token and bounce to /auth/error so "Try again" runs a clean
        // IdP redirect.
        if (token && !user) {
            logout();
            navigate('/auth/error', {
                replace: true,
                state: {
                    message: 'Your session expired or is missing profile data. Please sign in again.',
                },
            });
            return;
        }
        if (token) return;
        if (triedRef.current) return;
        triedRef.current = true;
        (async () => {
            try {
                const { pathname, search, hash } = window.location;
                await redirectToHostedUi(getCognitoConfig(), {
                    returnTo: `${pathname}${search}${hash}`,
                });
            } catch (err) {
                navigate('/auth/error', {
                    replace: true,
                    state: {
                        message: err instanceof Error
                            ? `Could not start sign-in: ${err.message}`
                            : 'Could not start sign-in.',
                    },
                });
            }
        })();
    }, [token, user, logout, navigate]);

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center text-sm text-text-subtle">
                    Redirecting to sign in…
                </div>
            </div>
        );
    }
    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center text-sm text-text-subtle">
                    Recovering your session…
                </div>
            </div>
        );
    }
    if (perm && !hasPerm(perm)) {
        return <Navigate to="/" replace state={{ from: location }} />;
    }
    return <>{children}</>;
}
